/**
 * ENTERPRISE TABLE - Composant réutilisable V1
 *
 * Table générique avec TanStack Table
 * - Tri des colonnes
 * - Sélection de lignes
 * - Pagination (côté table)
 * - Actions bulk
 * - Responsive
 */

import {
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type RowSelectionState,
  type SortingState,
} from '@tanstack/react-table';
import { ArrowDown, ArrowUp, ArrowUpDown, Loader2 } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';

type ColumnVisibilityState = Record<string, boolean>;

interface EnterpriseTableProps<TData> {
  data: TData[];
  columns: ColumnDef<TData>[];
  onRowSelectionChange?: (selectedRows: TData[]) => void;
  columnVisibility?: ColumnVisibilityState;
  onColumnVisibilityChange?: (visibility: ColumnVisibilityState) => void;
  pageSize?: number; // Nombre d'items par page
  currentPage?: number; // Page actuelle (1-indexed)
  onPageChange?: (page: number) => void; // Callback pour notifier le changement de page
  onPageSizeChange?: (pageSize: number) => void; // Callback pour notifier le changement de taille de page
  onSortedDataChange?: (sortedData: TData[]) => void; // Callback pour récupérer les données triées
  clearSelectionTrigger?: number; // Timestamp ou compteur pour forcer la réinitialisation de la sélection
  manualPagination?: boolean; // Si true, désactive la pagination TanStack Table (données déjà paginées)
  onRowClick?: (row: TData) => void; // Callback quand une ligne est cliquée
  getRowClassName?: (row: TData) => string | undefined; // Fonction pour ajouter une classe CSS conditionnelle à une ligne
  loading?: boolean; // Si true, affiche un overlay de chargement sur le tableau

  // ✅ Tri server-side
  manualSorting?: boolean; // Si true, désactive le tri TanStack Table (tri géré server-side)
  onSortChange?: (field: string | null, direction: 'asc' | 'desc') => void; // Callback quand le tri change
  activeSortField?: string | null;
  activeSortDirection?: 'asc' | 'desc';

  // ✅ Nouveau système de sélection multi-pages avec IDs persistants
  selectedIds?: Set<string>; // IDs sélectionnés (venant du parent)
  onToggleSelection?: (id: string) => void; // Callback pour basculer la sélection d'un item
  onToggleAllPage?: (items: TData[], select: boolean) => void; // Callback pour sélectionner/désélectionner la page
}

export function EnterpriseTable<TData>({
  data,
  columns,
  onRowSelectionChange,
  columnVisibility = {},
  onColumnVisibilityChange,
  pageSize = 10,
  currentPage = 1,
  onPageChange,
  onPageSizeChange,
  onSortedDataChange,
  clearSelectionTrigger,
  manualPagination = false,
  onRowClick,
  getRowClassName,
  loading = false,
  // Tri server-side
  manualSorting = false,
  onSortChange,
  activeSortField,
  activeSortDirection = 'asc',
  // Nouveau système de sélection multi-pages
  selectedIds,
  onToggleSelection: _onToggleSelection,
  onToggleAllPage: _onToggleAllPage,
}: EnterpriseTableProps<TData>) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  // Ref pour éviter la boucle de synchronisation
  const isInternalChange = useRef(false);
  const prevDataSignature = useRef<string>('');
  // Ref pour savoir si on synchronise depuis selectedIds (ne pas appeler onRowSelectionChange)
  const isSyncingFromSelectedIds = useRef(false);

  useEffect(() => {
    if (!manualSorting || activeSortField === undefined) return;
    const nextSorting: SortingState = activeSortField
      ? [{ id: activeSortField, desc: activeSortDirection === 'desc' }]
      : [];
    setSorting((current) =>
      current[0]?.id === nextSorting[0]?.id && current[0]?.desc === nextSorting[0]?.desc
        ? current
        : nextSorting
    );
  }, [activeSortDirection, activeSortField, manualSorting]);

  // ✅ Synchroniser rowSelection avec selectedIds quand :
  // - selectedIds change de l'extérieur (pas après un click interne)
  // - OU data change (changement de page)
  useEffect(() => {
    if (!selectedIds) return;

    // Calculer une signature des données pour détecter le changement de page
    const currentDataSignature = data.map((item: any) => item.id).join(',');
    const dataChanged = currentDataSignature !== prevDataSignature.current;
    prevDataSignature.current = currentDataSignature;

    // Si c'est un changement interne (click) ET les données n'ont pas changé, ne pas synchroniser
    if (isInternalChange.current && !dataChanged) {
      isInternalChange.current = false;
      return;
    }

    // Marquer qu'on synchronise depuis selectedIds (pour ne pas déclencher onRowSelectionChange)
    isSyncingFromSelectedIds.current = true;

    // Recalculer rowSelection à partir de selectedIds
    const newRowSelection: RowSelectionState = {};
    data.forEach((item: any, index) => {
      if (selectedIds.has(item.id)) {
        newRowSelection[index] = true;
      }
    });
    setRowSelection(newRowSelection);

    isInternalChange.current = false;
  }, [selectedIds, data]);

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      rowSelection,
      columnVisibility,
      pagination: {
        pageIndex: currentPage - 1, // Synchronisation directe depuis les props
        pageSize: pageSize,
      },
    },
    manualSorting: manualSorting, // Si true, tri géré server-side
    onSortingChange: (updater) => {
      const newSorting = typeof updater === 'function' ? updater(sorting) : updater;
      setSorting(newSorting);
      // En mode tri server-side, notifier le parent
      if (manualSorting && onSortChange) {
        const sort = newSorting[0];
        onSortChange(sort?.id || null, sort?.desc ? 'desc' : 'asc');
      }
    },
    onPaginationChange: (updater) => {
      // Quand la pagination change, notifier le parent
      if (typeof updater === 'function') {
        const newPagination = updater({
          pageIndex: currentPage - 1,
          pageSize: pageSize,
        });

        if (onPageChange && newPagination.pageIndex !== currentPage - 1) {
          onPageChange(newPagination.pageIndex + 1);
        }
        if (onPageSizeChange && newPagination.pageSize !== pageSize) {
          onPageSizeChange(newPagination.pageSize);
        }
      }
    },
    onColumnVisibilityChange: onColumnVisibilityChange
      ? (updater) => {
          if (typeof updater === 'function') {
            onColumnVisibilityChange(updater(columnVisibility));
          } else {
            onColumnVisibilityChange(updater);
          }
        }
      : undefined,
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    manualPagination: manualPagination, // Si true, données déjà paginées (mode server)
  });

  // Réinitialiser la sélection quand les données changent (après suppression par exemple)
  // ⚠️ NE PAS reset si selectedIds est fourni (nouveau système multi-pages)
  // Dans ce cas, la synchronisation est gérée par l'effet ci-dessus
  const dataSignature = useMemo(() => {
    return data.map((item: any) => item.id).join(',');
  }, [data]);

  useEffect(() => {
    // Si on utilise le nouveau système de sélection, ne pas reset automatiquement
    // La sélection est gérée par le parent via selectedIds
    if (!selectedIds) {
      setRowSelection({});
    }
  }, [dataSignature, selectedIds]);

  // Notifier le parent des lignes sélectionnées (effet séparé)
  useEffect(() => {
    // Si on synchronise depuis selectedIds (changement de page), ne pas notifier le parent
    // car cela écraserait les IDs sélectionnés avec les données de la nouvelle page
    if (isSyncingFromSelectedIds.current) {
      isSyncingFromSelectedIds.current = false;
      return;
    }

    // Si on utilise le nouveau système de sélection multi-pages avec selectedIds,
    // calculer la différence et toggler uniquement les changements pour ne pas écraser
    // les sélections des autres pages
    if (selectedIds && _onToggleSelection) {
      // Calculer les IDs actuellement sélectionnés sur cette page selon rowSelection
      const currentlySelectedOnPage = new Set<string>();
      Object.entries(rowSelection).forEach(([index, isSelected]) => {
        if (isSelected) {
          const item = data[Number(index)] as any;
          if (item?.id) {
            currentlySelectedOnPage.add(item.id);
          }
        }
      });

      // Pour chaque item de la page, vérifier s'il faut toggle
      data.forEach((item: any) => {
        const id = item.id;
        if (!id) return;

        const wasSelected = selectedIds.has(id);
        const isNowSelected = currentlySelectedOnPage.has(id);

        if (wasSelected !== isNowSelected) {
          // Toggle needed - marquer comme changement interne
          isInternalChange.current = true;
          _onToggleSelection(id);
        }
      });
      return;
    }

    // Ancien système : notifier avec toutes les lignes sélectionnées
    if (onRowSelectionChange) {
      // Marquer comme changement interne pour éviter la boucle de synchronisation
      isInternalChange.current = true;
      const selectedRows = table.getSelectedRowModel().rows.map((row) => row.original);
      onRowSelectionChange(selectedRows);
    }
  }, [rowSelection, table, onRowSelectionChange, selectedIds, _onToggleSelection, data]);

  // Réinitialiser la sélection quand le parent déclenche clearSelection
  useEffect(() => {
    if (clearSelectionTrigger && clearSelectionTrigger > 0) {
      setRowSelection({});
    }
  }, [clearSelectionTrigger]);

  // Mémoriser les données triées
  const sortedData = useMemo(() => {
    return table.getSortedRowModel().rows.map((row) => row.original);
  }, [table]);

  // Notifier le parent des données triées (pour l'export CSV)
  useEffect(() => {
    if (onSortedDataChange) {
      onSortedDataChange(sortedData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortedData]);

  return (
    <div className="relative flex flex-col w-full h-full">
      {/* Overlay de chargement */}
      {loading && (
        <div className="absolute inset-0 bg-white/60 z-20 flex items-center justify-center">
          <Loader2 className="size-8 text-zinc-400 animate-spin" />
        </div>
      )}

      {/* Header */}
      <div className="flex items-center w-full border-b border-zinc-200 px-page-padding sticky top-0 bg-white z-10">
        {table.getHeaderGroups().map((headerGroup) => (
          <div key={headerGroup.id} className="flex items-center w-full">
            {headerGroup.headers.map((header) => (
              <div
                key={header.id}
                className={`flex h-table-header items-center py-0 ${
                  header.column.id === 'actions' ? 'px-table-cell-x pr-0' : 'px-table-cell-x'
                }`}
                style={{
                  width:
                    header.column.id === 'select' || header.column.id === 'actions'
                      ? `${header.getSize()}px`
                      : undefined,
                  minWidth:
                    header.column.id !== 'select' && header.column.id !== 'actions'
                      ? header.column.columnDef.minSize
                        ? `${header.column.columnDef.minSize}px`
                        : '100px' // Largeur minimale par défaut pour éviter que ça soit trop serré
                      : undefined,
                  flexGrow: header.column.id !== 'select' && header.column.id !== 'actions' ? 1 : 0,
                  flexShrink:
                    header.column.id !== 'select' && header.column.id !== 'actions' ? 1 : 0,
                  flexBasis:
                    header.column.id !== 'select' && header.column.id !== 'actions'
                      ? header.getSize() !== 150
                        ? `${header.getSize()}px`
                        : '0'
                      : undefined,
                  marginLeft: header.column.id === 'actions' ? 'auto' : undefined,
                  marginRight: header.column.id === 'actions' ? '1rem' : undefined,
                }}
              >
                {header.isPlaceholder ? null : (
                  <div
                    className={`flex items-center gap-2 flex-grow h-full text-table-header text-zinc-500 ${
                      header.column.getCanSort()
                        ? 'cursor-pointer select-none hover:text-zinc-900'
                        : ''
                    } ${header.column.id === 'actions' ? 'justify-end' : 'justify-start'}`}
                    onClick={
                      manualSorting && onSortChange
                        ? header.column.getCanSort()
                          ? () => {
                              const colId = header.column.id;
                              if (activeSortField !== colId) {
                                onSortChange(colId, 'asc');
                              } else if (activeSortDirection === 'asc') {
                                onSortChange(colId, 'desc');
                              } else {
                                onSortChange(null, 'asc');
                              }
                            }
                          : undefined
                        : header.column.getToggleSortingHandler()
                    }
                  >
                    <span>{flexRender(header.column.columnDef.header, header.getContext())}</span>
                    {header.column.getCanSort() && (
                      <span className="flex items-center">
                        {manualSorting ? (
                          activeSortField === header.column.id ? (
                            activeSortDirection === 'asc' ? (
                              <ArrowUp className="size-4" />
                            ) : (
                              <ArrowDown className="size-4" />
                            )
                          ) : (
                            <ArrowUpDown className="size-4 opacity-0 group-hover:opacity-50" />
                          )
                        ) : header.column.getIsSorted() === 'asc' ? (
                          <ArrowUp className="size-4" />
                        ) : header.column.getIsSorted() === 'desc' ? (
                          <ArrowDown className="size-4" />
                        ) : (
                          <ArrowUpDown className="size-4 opacity-0 group-hover:opacity-50" />
                        )}
                      </span>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        ))}
      </div>

      {/* Body */}
      <div className="px-page-padding">
        {table.getRowModel().rows.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12">
            <p className="text-sm text-zinc-500">Aucune donnée à afficher</p>
          </div>
        ) : (
          table.getRowModel().rows.map((row) => {
            const rowClassName = getRowClassName?.(row.original);
            return (
              <div
                key={row.id}
                className={`flex items-center w-full border-b border-zinc-200 ${
                  onRowClick ? 'cursor-pointer hover:bg-zinc-50 transition-colors' : ''
                } ${rowClassName || ''}`}
                onClick={() => onRowClick?.(row.original)}
              >
                {row.getVisibleCells().map((cell) => (
                  <div
                    key={cell.id}
                    className={`flex gap-2.5 min-h-table-row items-center ${
                      cell.column.id === 'actions' ? 'p-table-cell-x pr-0' : 'p-table-cell-x'
                    }`}
                    style={{
                      width:
                        cell.column.id === 'select' || cell.column.id === 'actions'
                          ? `${cell.column.getSize()}px`
                          : undefined,
                      minWidth:
                        cell.column.id !== 'select' && cell.column.id !== 'actions'
                          ? cell.column.columnDef.minSize
                            ? `${cell.column.columnDef.minSize}px`
                            : '100px'
                          : undefined,
                      flexGrow: cell.column.id !== 'select' && cell.column.id !== 'actions' ? 1 : 0,
                      flexShrink:
                        cell.column.id !== 'select' && cell.column.id !== 'actions' ? 1 : 0,
                      flexBasis:
                        cell.column.id !== 'select' && cell.column.id !== 'actions'
                          ? cell.column.getSize() !== 150
                            ? `${cell.column.getSize()}px`
                            : '0'
                          : undefined,
                      marginLeft: cell.column.id === 'actions' ? 'auto' : undefined,
                      marginRight: cell.column.id === 'actions' ? '1rem' : undefined,
                    }}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </div>
                ))}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
